import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormWithPrivacyHandler } from 'components/molecules/custom-form';
import { useCurrentSpace } from 'components/organisms/spaces/context';
import { EditorModes } from 'data/documents';
import { useTranslation } from 'hooks/use-translation';
import { CommentService } from '../../services/comment';
import { TextFromDoc } from '../doc-discussions/discussions-components';
import {
  createADiscussionOnStore,
  useDiscussionsDispatch,
} from '../doc-discussions/discussions-store';
import styles from './comment-form.module.css';

interface Props {
  show: boolean;
  onClose: () => void;
  editor: Editor | null;
  documentData: {
    projectId: number;
    docId: number;
  };
  docContent: string;
}

const CommentForm: FC<Props> = ({
  show,
  editor,
  onClose,
  documentData,
  docContent,
}: Props) => {
  const { projectId, docId } = documentData;
  const [isPrivate, setIsPrivate] = useState(false);
  const [isAlwaysPrivate, setAlwaysPrivate] = useState(false);

  const { private: isDocPrivate } = useCurrentSpace();

  const commentFormRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDiscussionsDispatch();
  const addCommentMsg = useTranslation('add_comment');

  useEffect(() => {
    if (show) {
      commentFormRef.current?.scrollIntoView();
    }
  }, [show, commentFormRef, docContent]);

  useEffect(() => {
    const fromPrivateEditor = editor?.sourceElement.id === EditorModes.Private;

    setAlwaysPrivate(fromPrivateEditor);
    setIsPrivate(isDocPrivate);
  }, [editor, isDocPrivate]);

  const onCloseForm = () => {
    const options = {
      commentId: '0',
    };

    editor?.execute('removeCommentCommand', options);
    onClose();
  };
  const onSubmit = (commentContent: string) => {
    const createComment = async () => {
      const response = await createADiscussionOnStore(dispatch, {
        projectId,
        docId,
        content: commentContent,
        isPrivate,
      });
      const commentId = `rl-comment-${response?.comment?.commentThreadId}`;
      const options = {
        commentId,
        commentType: isPrivate ? EditorModes.Private : EditorModes.Public,
      };

      editor?.execute('insertCommentCommand', options);
      editor?.editing.view.focus();
      CommentService.updateContentIdMap({
        [response?.comment?.commentThreadId]: {
          content: docContent,
          editorType: editor?.sourceElement.id || EditorModes.Public,
          isActive: true,
          docId,
        },
      });
      onClose();
    };

    createComment();
  };

  return show ? (
    <div className={styles.wrapper} ref={commentFormRef}>
      <TextFromDoc
        content={docContent}
        contentType={isPrivate ? 'private' : 'public'}
      />
      <FormWithPrivacyHandler
        isPrivate={isPrivate}
        initialFormVisibility
        hidePrivateToggler={isAlwaysPrivate}
        focusTrigger={docContent}
        placeholder={addCommentMsg}
        onSubmit={onSubmit}
        onClose={onCloseForm}
        onPrivateBtnClick={() => setIsPrivate(!isPrivate)}
        projectId={projectId}
        isAvatarInvisible
      />
    </div>
  ) : null;
};

export { CommentForm };
